import { ListItem, ListItemButton, ListItemIcon, ListItemText, Collapse, Box } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { ReactElement } from 'react';
import { theme } from '@theguarantors/tg-package-material-ui';

interface Props {
  open: boolean;
  text?: string;
  displayText?: ReactElement;
  action: () => void;
  icon?: JSX.Element;
  ariaLabel?: string;
  selected?: boolean;
  disabled?: boolean;
  disabledTooltip?: string;
  outlined?: boolean;
}

const SideMenuItem = ({
  open,
  text,
  displayText,
  action,
  icon,
  ariaLabel,
  selected,
  disabled,
  disabledTooltip,
  outlined,
}: Props) => {
  const baseTextColor = theme.palette.text.secondary;
  const disabledTextColor = theme.palette.neutralGrey[700];

  const tooltipTitle = disabled && disabledTooltip ? disabledTooltip : text;
  const disableTooltipHover = disabled && disabledTooltip ? false : open || !text;

  return (
    <ListItem
      disablePadding
      sx={{
        minWidth: '48px',
        [':not(:first-child)']: {
          marginTop: '4px !important',
        },
      }}
    >
      <Tooltip
        arrow
        title={tooltipTitle}
        disableHoverListener={disableTooltipHover}
        placement="right"
      >
        <Box sx={{ width: '100%' }}>
          <ListItemButton
            onClick={action}
            className={open ? 'list-item-button-open' : 'list-item-button-close'}
            disabled={disabled}
            sx={{
              outline: outlined ? '1px solid white' : 'none',
              opacity: `${disabled ? '0.5' : '1'} !important`,
              justifyContent: !icon && outlined ? 'center' : 'flex-start',
              alignItems: 'center',
              padding: 0,
              borderRadius: '10px',
              margin: '0px',
              height: '48px',
              textAlign: icon ? 'left' : 'center',
              backgroundColor: `rgba(8, 10, 45, ${selected ? 0.12 : 0})`,
              [':hover']: {
                backgroundColor: 'rgba(8, 10, 45, 0.12)',
              },
              [':active']: {
                backgroundColor: 'rgba(8, 10, 45, 0.2)',
              },
            }}
            aria-label={ariaLabel ?? text}
          >
            {icon && (
              <ListItemIcon
                sx={{
                  justifyContent: text || !open ? 'center' : 'flex-end',
                  alignItems: 'center',
                  minWidth: 'unset',
                  height: '24px',
                  width: '24px',
                  margin: '12px',
                  ['svg']: {
                    fill: `${disabled ? disabledTextColor : baseTextColor} !important`,
                    fontSize: '22px',
                  },
                }}
              >
                {icon}
              </ListItemIcon>
            )}

            {text ? (
              <Collapse
                in={open}
                orientation="horizontal"
                timeout={300}
                sx={{
                  margin: '12px',
                  flex: 1,
                  alignItems: 'center',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s linear',
                }}
              >
                <ListItemText
                  primary={displayText ?? text}
                  slotProps={{
                    primary: {
                      color: disabled ? disabledTextColor : baseTextColor,
                      sx: {
                        lineHeight: '24px !important',
                        fontSize: '16px !important',
                        height: '24px !important',
                      },
                    },
                  }}
                  sx={{
                    margin: 0,
                  }}
                />
              </Collapse>
            ) : null}
          </ListItemButton>
        </Box>
      </Tooltip>
    </ListItem>
  );
};

export default SideMenuItem;
